import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface GeneratedContent {
  title?: string;
  description?: string;
  altText?: string;
  imageName: string;
}

interface ResultsTableContentProps {
  results: GeneratedContent[];
}

export const ResultsTableContent: React.FC<ResultsTableContentProps> = ({ results }) => {
  const [editingContent, setEditingContent] = useState<{
    index: number;
    field: keyof GeneratedContent;
    value: string;
  } | null>(null);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleEdit = (index: number, field: keyof GeneratedContent, value: string) => {
    setEditingContent({ index, field, value });
  };

  const handleSave = () => {
    if (!editingContent) return;
    
    const { index, field, value } = editingContent;
    results[index] = { ...results[index], [field]: value };
    setEditingContent(null);
    toast.success('Changes saved successfully');
  };

  const renderActions = (index: number, field: keyof GeneratedContent, value?: string) => {
    if (field === 'imageName' || !value) return null;
    
    return (
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => copyToClipboard(value)}
          className="h-8 w-8 p-0"
        >
          <Copy className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleEdit(index, field, value)}
          className="h-8 w-8 p-0"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const columnOrder: (keyof GeneratedContent)[] = ['imageName', 'title', 'description', 'altText'];
  const columnHeaders: Record<keyof GeneratedContent, string> = {
    imageName: 'Image',
    title: 'Title',
    description: 'Description',
    altText: 'Alt Text'
  };

  const renderPreview = (text?: string) => {
    if (!text) return '-';
    return text.length > 50 ? `${text.substring(0, 50)}...` : text;
  };

  return (
    <>
      <ScrollArea className="w-full whitespace-nowrap rounded-md border">
        <div className="relative w-full">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-sage-50 text-sage-700">
                {columnOrder.map((column) => (
                  <th key={column} className="p-4 text-left font-medium">
                    {columnHeaders[column]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-sage-100">
              {results.map((result, index) => (
                <tr key={index} className="hover:bg-sage-50/50">
                  {columnOrder.map((column) => (
                    <td key={column} className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <span>{renderPreview(result[column])}</span>
                        {renderActions(index, column, result[column])}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <Dialog open={editingContent !== null} onOpenChange={() => setEditingContent(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              Edit {editingContent?.field?.charAt(0).toUpperCase() + editingContent?.field?.slice(1)}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Content</Label>
              <Textarea
                value={editingContent?.value || ''}
                onChange={(e) => setEditingContent(prev => 
                  prev ? { ...prev, value: e.target.value } : null
                )}
                className="min-h-[200px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingContent(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};